import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/LanguageContext";
import { IMAGES } from "@/content";

const ease = [0.22, 1, 0.36, 1];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Line = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-screen min-h-[680px] overflow-hidden bg-navy">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroParis} alt="Paris at dawn" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/40 to-navy" />
        <div className="absolute inset-0 bg-navy/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="tricolore"><span className="bg-frblue" /><span className="bg-white" /><span className="bg-frred" /></span>
          <span className="text-gold text-xs tracking-luxe uppercase">{t.hero.eyebrow}</span>
        </motion.div>

        <h1 className="font-serif-lux text-white font-light leading-[0.98] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl">
          <Line delay={0.5}>{t.hero.l1}</Line>
          <Line delay={0.65}><span className="italic text-gold-light">{t.hero.l2}</span></Line>
          <Line delay={0.8}>{t.hero.l3}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}
          className="mt-6 max-w-xl text-white/70 text-sm md:text-base font-light leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("contact")}
            className="btn-lux bg-gold text-navy hover:bg-gold-light px-8 py-4 text-xs tracking-luxe uppercase font-medium"
          >
            {t.hero.cta1}
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("contact")}
            className="btn-lux border border-white/40 text-white hover:border-gold hover:text-gold px-8 py-4 text-xs tracking-luxe uppercase"
          >
            {t.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-white/50 text-[10px] tracking-luxe uppercase">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="w-px h-10 bg-gold/60" />
      </motion.div>
    </section>
  );
}
